<script setup lang="ts">
import {
  CalendarOutlined,
  DollarOutlined,
  HomeOutlined,
  ReloadOutlined,
  TeamOutlined,
} from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { formatPrice } from '@/lib/currency'
import type { MonthlyRevenue, RecentBooking, UpcomingCheckin } from '@/services/dashboard'
import {
  fetchDashboardStats,
  fetchMonthlyRevenue,
  fetchRecentBookings,
  fetchUpcomingCheckins,
} from '@/services/dashboard'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const auth = useAuthStore()

const greeting = ref('')
const stats = ref<Array<{ title: string; value: string; icon: any; color: string }>>([])
const recentBookings = ref<RecentBooking[]>([])
const upcomingCheckins = ref<UpcomingCheckin[]>([])
const monthlyRevenue = ref<MonthlyRevenue[]>([])
const loading = ref(true)

const userName = computed(() => auth.user?.name?.split(' ')[0] || '')

const maxRevenue = computed(() => {
  const revenues = monthlyRevenue.value.map((m) => m.revenue)
  return revenues.length ? Math.max(...revenues) : 1
})

async function loadDashboard() {
  loading.value = true
  try {
    const [dashboardStats, bookings, checkins, rev] = await Promise.all([
      fetchDashboardStats(),
      fetchRecentBookings(5),
      fetchUpcomingCheckins(5),
      fetchMonthlyRevenue(12),
    ])

    stats.value = [
      {
        title: 'Total Properties',
        value: String(dashboardStats.totalProperties),
        icon: HomeOutlined,
        color: '#1677ff',
      },
      {
        title: 'Occupancy Rate',
        value: `${dashboardStats.occupancyRate}%`,
        icon: CalendarOutlined,
        color: '#52c41a',
      },
      {
        title: 'Monthly Revenue',
        value: formatPrice(dashboardStats.monthlyRevenue),
        icon: DollarOutlined,
        color: '#722ed1',
      },
      {
        title: 'Upcoming Bookings',
        value: String(dashboardStats.upcomingBookings),
        icon: TeamOutlined,
        color: '#eb2f96',
      },
    ]

    recentBookings.value = bookings
    upcomingCheckins.value = checkins
    monthlyRevenue.value = rev
  } catch (e: any) {
    message.error(e?.message || 'Failed to load dashboard')
    stats.value = [
      { title: 'Total Properties', value: '0', icon: HomeOutlined, color: '#1677ff' },
      { title: 'Occupancy Rate', value: '0%', icon: CalendarOutlined, color: '#52c41a' },
      { title: 'Monthly Revenue', value: formatPrice(0), icon: DollarOutlined, color: '#722ed1' },
      { title: 'Upcoming Bookings', value: '0', icon: TeamOutlined, color: '#eb2f96' },
    ]
    recentBookings.value = []
    upcomingCheckins.value = []
    monthlyRevenue.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  const hour = new Date().getHours()
  if (hour < 12) greeting.value = 'Good morning'
  else if (hour < 18) greeting.value = 'Good afternoon'
  else greeting.value = 'Good evening'
  loadDashboard()
})

const statRoutes = ['/properties', '/bookings', '/billing', '/bookings']

const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    confirmed: 'green',
    pending: 'orange',
    cancelled: 'red',
    completed: 'default',
    'no-show': 'gray',
  }
  return colors[status] || 'default'
}

const goToRoute = (path: string) => {
  router.push(path)
}
</script>

<template>
  <div style="padding: 16px">
    <div style="margin-bottom: 16px">
      <a-flex justify="space-between" align="center" wrap="wrap" gap="small">
        <div>
          <h1 style="font-size: 24px; font-weight: 700; margin: 0">{{ greeting }}{{ userName ? `, ${userName}` : '' }} 👋</h1>
          <p style="color: var(--text-secondary); margin: 4px 0 0">Here's what's happening with your properties today.</p>
        </div>
        <a-button @click="loadDashboard" :loading="loading">
          <ReloadOutlined /> Refresh
        </a-button>
      </a-flex>
    </div>

    <a-row :gutter="[16, 16]" style="margin-bottom: 24px; align-items: stretch">
      <a-col :xs="24" :sm="12" :md="6" v-for="i in 4" :key="i" style="display: flex">
        <a-card
          hoverable
          style="flex: 1; display: flex; flex-direction: column; min-width: 0; overflow: hidden"
          :body-style="{ flex: 1, display: 'flex', alignItems: 'center', minWidth: '0', overflow: 'hidden' }"
          @click="goToRoute(statRoutes[i - 1])"
        >
          <a-skeleton :loading="loading" active :paragraph="{ rows: 1, width: '60%' }" :title="{ width: '50%' }" style="width: 100%">
            <div v-if="stats[i - 1]" style="display: flex; align-items: flex-start; gap: 12px; min-width: 0; width: 100%">
              <component
                :is="stats[i - 1].icon"
                :style="{ fontSize: '24px', color: stats[i - 1].color, flexShrink: 0, marginTop: '2px' }"
              />
              <div style="min-width: 0; flex: 1; overflow: hidden">
                <div
                  style="
                    font-size: 14px;
                    color: var(--text-secondary);
                    margin-bottom: 4px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                  "
                >
                  {{ stats[i - 1].title }}
                </div>
                <div
                  :style="{
                    fontSize: '28px',
                    fontWeight: 700,
                    color: stats[i - 1].color,
                    lineHeight: '1.2',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    minWidth: '0',
                  }"
                >
                  {{ stats[i - 1].value }}
                </div>
              </div>
            </div>
          </a-skeleton>
        </a-card>
      </a-col>
    </a-row>

    <a-card title="Revenue (last 12 months)" style="margin-bottom: 16px">
      <template v-if="loading">
        <div style="height: 240px; display: flex; align-items: flex-end; gap: 6px; padding: 16px 0">
          <div v-for="i in 12" :key="i" style="flex: 1; display: flex; flex-direction: column; align-items: center; height: 100%; justify-content: flex-end">
            <a-skeleton-button active size="small" style="width: 24px; height: 16px; margin-bottom: 4px" />
            <a-skeleton-button active size="small" :style="{ width: '100%', maxWidth: '40px', height: Math.floor(Math.random() * 60 + 40) + 'px', borderRadius: '4px 4px 0 0' }" />
            <a-skeleton-button active size="small" style="width: 20px; height: 10px; margin-top: 6px" />
          </div>
        </div>
      </template>
      <div v-else-if="monthlyRevenue.length > 0" style="height: 240px; display: flex; align-items: flex-end; gap: 6px; padding: 16px 0">
          <div
            v-for="m in monthlyRevenue"
            :key="m.month"
            style="flex: 1; display: flex; flex-direction: column; align-items: center; height: 100%; justify-content: flex-end"
          >
            <div style="font-size: 11px; color: var(--text-secondary); margin-bottom: 4px; white-space: nowrap">
              {{ m.revenue >= 1000 ? '₱' + (m.revenue / 1000).toFixed(1) + 'k' : '₱' + m.revenue }}
            </div>
            <div
              :style="{
                width: '100%',
                maxWidth: 40,
                height: m.revenue > 0 ? Math.max((m.revenue / maxRevenue) * 180, 4) + 'px' : '4px',
                backgroundColor: '#1677ff',
                borderRadius: '4px 4px 0 0',
                transition: 'height 0.3s',
              }"
            ></div>
            <div style="font-size: 11px; color: var(--text-secondary); margin-top: 6px">{{ m.label }}</div>
          </div>
        </div>
        <div v-else style="height: 200px; display: flex; align-items: center; justify-content: center; color: var(--text-secondary)">
          No revenue data yet
        </div>
    </a-card>

    <a-row :gutter="[16, 16]">
      <a-col :xs="24" :lg="16">
        <a-card title="Recent Bookings">
          <a-skeleton :loading="loading" active :paragraph="{ rows: 4, width: ['60%', '50%', '70%', '40%'] }" :title="{ width: '30%' }">
            <a-table
            :data-source="recentBookings"
            :pagination="false"
            size="small"
            :row-key="(r: RecentBooking) => r.id"
          >
            <a-table-column title="Guest" data-index="guest_name" />
            <a-table-column title="Property" data-index="property_title" />
            <a-table-column title="Check-in" data-index="check_in" />
            <a-table-column title="Check-out" data-index="check_out" />
            <a-table-column title="Status" data-index="status">
              <template #default="{ text }">
                <a-tag :color="getStatusColor(text)">{{ text }}</a-tag>
              </template>
            </a-table-column>
          </a-table>
          <div v-if="!loading && recentBookings.length === 0" style="padding: 24px; text-align: center; color: var(--text-secondary)">
            No bookings yet. <a-button type="link" style="padding: 0" @click="goToRoute('/bookings')">Create your first booking</a-button>
          </div>
          </a-skeleton>
        </a-card>
      </a-col>

      <a-col :xs="24" :lg="8">
        <a-card title="Upcoming Check-ins / Check-outs">
          <a-skeleton :loading="loading" active :paragraph="{ rows: 3 }" :title="false">
          <a-timeline v-if="upcomingCheckins.length > 0">
            <a-timeline-item
              v-for="(event, i) in upcomingCheckins"
              :key="i"
              :color="event.type === 'checkin' ? 'blue' : 'gray'"
            >
              <div style="display: flex; justify-content: space-between; align-items: flex-start">
                <div>
                  <strong>{{ event.guest_name }}</strong>
                  <div style="font-size: 12px">{{ event.property_title }}</div>
                  <div style="font-size: 12px; color: var(--text-secondary)">
                    <span>In: {{ event.check_in }}</span><br />
                    <span>Out: {{ event.check_out }}</span>
                  </div>
                </div>
                <a-tag :color="event.type === 'checkin' ? 'blue' : 'default'">
                  {{ event.type === 'checkin' ? 'Check-in' : 'Check-out' }}
                </a-tag>
              </div>
            </a-timeline-item>
          </a-timeline>
          <div v-else-if="!loading" style="padding: 24px; text-align: center; color: var(--text-secondary)">
            No upcoming events
          </div>
          </a-skeleton>
        </a-card>

        <a-card title="Quick Actions" style="margin-top: 16px">
          <a-row :gutter="[8, 8]">
            <a-col :span="12">
              <a-button type="primary" block @click="goToRoute('/bookings')">
                <template #icon><CalendarOutlined /></template>
                New Booking
              </a-button>
            </a-col>
            <a-col :span="12">
              <a-button type="default" block @click="goToRoute('/properties')">
                <template #icon><HomeOutlined /></template>
                Add Property
              </a-button>
            </a-col>
          </a-row>
        </a-card>
      </a-col>
    </a-row>
  </div>
</template>
